import { createColumnHelper } from '@tanstack/react-table';

import { Card, CardHeader } from '@/components/core/card';
import { DataTable, useDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';
import { SkuBadge } from '@/pages/seller/components/sku-badge';
import { formatNumber } from '@/shared/helpers';

export type ReturnLine = {
  id: string;
  skuId: string;
  skuName: string;
  description: string;
  qtyExpected: number;
  qtyReceived: number;
};

const columnHelper = createColumnHelper<ReturnLine>();

const columns = [
  columnHelper.accessor('description', {
    header: 'SKU / description',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <SkuBadge name={row.original.skuName} />
        <span className="truncate">{getValue()}</span>
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Qty expected',
    cell: ({ getValue }) => formatNumber(getValue(), 0),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyReceived', {
    header: 'Qty received',
    cell: ({ getValue }) => formatNumber(getValue(), 0),
    meta: { align: 'right' },
  }),
];

type ReturnLinesCardProps = {
  title: string;
  lines: ReturnLine[];
  isLoading?: boolean;
  hasError?: boolean;
};

export function ReturnLinesCard({ title, lines, isLoading, hasError }: ReturnLinesCardProps) {
  const table = useDataTable<ReturnLine>({
    data: lines,
    columns,
    isLoading,
    hasError,
    // This card has no pagination controls, so opt out of client pagination — otherwise
    // TanStack's default 10-row page size silently hides rows.
    manualPagination: true,
    manualSorting: false,
  });

  return (
    <Card aria-label={title}>
      <CardHeader>
        <DataTableCardTitle table={table}>{title}</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table} emptyMessage="No items found">
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
